import React from 'react';
import { Users, Plus, X, Key, Trash2, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/Button';
import { CollapsibleSection } from '../../../components/ui/CollapsibleSection';
import { Avatar } from '../../../components/ui/Avatar';
import { InputField, SelectField } from '../../../components/ui/FormControls';

export const UserManager = ({
    users,
    newUser,
    setNewUser,
    isCreatingUser,
    setIsCreatingUser,
    handleCreateUser,
    openPasswordModal,
    openCameraPermissionsModal,
    setConfirmConfig,
    showToast,
    fetchUsers,
    currentUser,
    cameras,
    token,
    isOpen,
    onToggle
}) => {
    const { t } = useTranslation();

    // Live View performance profile (per user, admin-assignable):
    //   auto  → lightweight grid on iPhone/iPad Safari, full H.264 elsewhere
    //   light → lightweight grid on every device (full stream only on focus)
    //   full  → always full H.264 (override for capable devices)
    const LIVE_VIEW_PROFILES = [
        { value: 'auto', label: 'Auto' },
        { value: 'light', label: 'Light' },
        { value: 'full', label: 'Full' },
    ];

    const updateLiveViewProfile = async (u, value) => {
        try {
            const res = await fetch(`/api/users/${u.id}/preferences`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ live_view_profile: value }),
            });
            if (res.ok) {
                showToast(`Live View profile updated for ${u.username}`, 'success');
                fetchUsers();
            } else {
                const data = await res.json().catch(() => ({}));
                showToast('Failed: ' + (data.detail || res.status), 'error');
            }
        } catch (err) {
            showToast('Error: ' + err.message, 'error');
        }
    };

    const liveViewSelect = (u) => (
        <select
            value={u.live_view_profile || 'auto'}
            onChange={(e) => updateLiveViewProfile(u, e.target.value)}
            title="Live View performance: Auto = light on iPhone/iPad, full elsewhere · Light = light on every device · Full = always full quality"
            className="bg-card border border-border rounded-md text-xs py-1.5 pl-2 pr-6 cursor-pointer focus:ring-1 focus:ring-primary"
        >
            {LIVE_VIEW_PROFILES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
            ))}
        </select>
    );

    return (
        <CollapsibleSection
            id="users"
            title={t('settings.users.title')}
            description={t('settings.users.description')}
            icon={<Users className="w-6 h-6" />}
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <div className="flex justify-end mb-4 h-11">
                <Button
                    variant={isCreatingUser ? "ghost" : "default"}
                    onClick={() => setIsCreatingUser(!isCreatingUser)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 h-full px-6 font-bold"
                >
                    {isCreatingUser ? <X className="w-5 h-5 shrink-0" /> : <Plus className="w-5 h-5 shrink-0" />}
                    {isCreatingUser ? t('common.cancel') : t('settings.users.addUser')}
                </Button>
            </div>

            {isCreatingUser && (
                <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputField
                            label={t('settings.users.usernameLabel')}
                            value={newUser.username}
                            onChange={(val) => setNewUser({ ...newUser, username: val })}
                            placeholder="johndoe"
                            required
                        />
                        <InputField
                            label={t('settings.users.emailLabel')}
                            type="email"
                            value={newUser.email}
                            onChange={(val) => setNewUser({ ...newUser, email: val })}
                            placeholder="john@example.com"
                        />
                        <InputField
                            label={t('settings.users.passwordLabel')}
                            type="password"
                            value={newUser.password}
                            onChange={(val) => setNewUser({ ...newUser, password: val })}
                            placeholder="••••••••"
                            required
                        />
                        <SelectField
                            label={t('settings.users.roleLabel')}
                            value={newUser.role}
                            onChange={(val) => setNewUser({ ...newUser, role: val })}
                            options={[
                                { value: 'viewer', label: t('settings.users.roleViewer') },
                                { value: 'admin', label: t('settings.users.roleAdmin') }
                            ]}
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button type="submit" className="w-full sm:w-auto h-11 px-8 font-bold">{t('settings.users.createUser')}</Button>
                    </div>
                </form>
            )}

            {/* Mobile: User Cards (Zero Horizontal Scroll) */}
            <div className="grid grid-cols-1 gap-3 sm:hidden mt-4">
                {users.map(u => (
                    <div key={u.id} className="p-4 bg-muted/10 border border-border/50 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Avatar user={u} size="xs" />
                                <div className="min-w-0">
                                    <p className="font-bold text-sm truncate">{u.username}</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-bold uppercase tracking-wider ${u.role === 'admin' ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-muted text-muted-foreground border border-border/50'}`}>
                                        {u.role}
                                    </span>
                                </div>
                            </div>
                            {u.id === currentUser.id && (
                                <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-lg font-bold uppercase tracking-tight">{t('settings.users.youBadge')}</span>
                            )}
                        </div>
                        <div className="flex items-center justify-between text-xs pt-3 border-t border-border/30">
                            <span className="text-muted-foreground font-medium">Live View</span>
                            {liveViewSelect(u)}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => openPasswordModal(u)}
                                className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                                title={t('settings.users.changePassword')}
                            >
                                <Key className="w-5 h-5" />
                            </button>
                            {u.role !== 'admin' && u.id !== currentUser.id && (
                                <button
                                    onClick={() => openCameraPermissionsModal(u)}
                                    className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                                    title="Camera Permissions"
                                >
                                    <Shield className="w-5 h-5" />
                                </button>
                            )}
                            {u.id !== currentUser.id && (
                                <button
                                    onClick={() => {
                                        setConfirmConfig({
                                            isOpen: true,
                                            title: t('settings.users.deleteUser'),
                                            message: t('settings.users.deleteConfirm', { username: u.username }),
                                            onConfirm: async () => {
                                                try {
                                                    const res = await fetch(`/api/users/${u.id}`, {
                                                        method: 'DELETE',
                                                        headers: { Authorization: `Bearer ${token}` }
                                                    });
                                                    if (res.ok) {
                                                        showToast(t('settings.users.deletedSuccess'), 'success');
                                                        fetchUsers();
                                                    } else {
                                                        const data = await res.json();
                                                        showToast('Failed: ' + data.detail, 'error');
                                                    }
                                                } catch (err) {
                                                    showToast('Error: ' + err.message, 'error');
                                                }
                                                setConfirmConfig({ isOpen: false });
                                            },
                                            onCancel: () => setConfirmConfig({ isOpen: false })
                                        });
                                    }}
                                    className="p-2 hover:bg-red-100 text-red-500 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                                    title={t('settings.users.deleteUser')}
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="hidden sm:block w-full overflow-x-auto mt-4 rounded-lg border border-border">
                <div className="min-w-[600px]">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-left">
                            <tr>
                                <th className="p-3 font-medium text-muted-foreground">{t('settings.users.colUsername')}</th>
                                <th className="p-3 font-medium text-muted-foreground">{t('settings.users.colRole')}</th>
                                <th className="p-3 font-medium text-muted-foreground">Live View</th>
                                <th className="p-3 font-medium text-muted-foreground hidden sm:table-cell">{t('settings.users.colCreated')}</th>
                                <th className="p-3 font-medium text-muted-foreground text-right">{t('settings.users.colActions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id} className="border-t border-border hover:bg-muted/10">
                                    <td className="p-3 font-medium flex items-center gap-3 overflow-hidden">
                                        <Avatar user={u} size="xs" />
                                        <span className="truncate">{u.username}</span>
                                        {u.id === currentUser.id && <span className="text-[10px] bg-primary/20 text-primary px-1.5 shrink-0 rounded">{t('settings.users.youBadge')}</span>}
                                    </td>
                                    <td className="p-3">
                                        <span className={'inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ' +
                                            (u.role === 'admin' ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-muted text-muted-foreground border border-border/50')
                                        }>
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="p-3">
                                        {liveViewSelect(u)}
                                    </td>
                                    <td className="p-3 text-muted-foreground hidden sm:table-cell">
                                        {new Date(u.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-2 pr-1">
                                            <button
                                                onClick={() => openPasswordModal(u)}
                                                className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors"
                                                title={t('settings.users.changePassword')}
                                            >
                                                <Key className="w-5 h-5" />
                                            </button>
                                            {u.role !== 'admin' && u.id !== currentUser.id && (
                                                <button
                                                    onClick={() => openCameraPermissionsModal(u)}
                                                    className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition-colors"
                                                    title="Camera Permissions"
                                                >
                                                    <Shield className="w-5 h-5" />
                                                </button>
                                            )}
                                            {u.id !== currentUser.id && (
                                                <button
                                                    onClick={() => {
                                                        setConfirmConfig({
                                                            isOpen: true,
                                                            title: t('settings.users.deleteUser'),
                                                            message: t('settings.users.deleteConfirm', { username: u.username }),
                                                            onConfirm: async () => {
                                                                try {
                                                                    const res = await fetch(`/api/users/${u.id}`, {
                                                                        method: 'DELETE',
                                                                        headers: { Authorization: `Bearer ${token}` }
                                                                    });
                                                                    if (res.ok) {
                                                                        showToast(t('settings.users.deletedSuccess'), 'success');
                                                                        fetchUsers();
                                                                    } else {
                                                                        const data = await res.json();
                                                                        showToast('Failed: ' + data.detail, 'error');
                                                                    }
                                                                } catch (err) {
                                                                    showToast('Error: ' + err.message, 'error');
                                                                }
                                                                setConfirmConfig({ isOpen: false });
                                                            },
                                                            onCancel: () => setConfirmConfig({ isOpen: false })
                                                        });
                                                    }}
                                                    className="p-2 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                                                    title={t('settings.users.deleteUser')}
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">{t('settings.users.noUsers')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CollapsibleSection>
    );
};
